import React, { useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import AloDropdown from '../YogaUI/alo-ui/AloDropdown';
import ReactDOM from 'react-dom';
import { InitialUrlSearchParams } from '../../../../utils/environment';
import { prependLocaleToPath } from '../../../../utils/localization';
import { IS_NATIVE_APP_CLIENT } from '../../../../utils/helpers';

const urlParams = InitialUrlSearchParams;

const AccountMenu = ({ customer }: { customer: CustomerInfo | null }) => {
  const shouldShowDropdownMenu = useMediaQuery('(max-width: 992.98px)', { noSsr: true });

  const [activeKey, setActiveKey] = useState<number>(-1);
  const [shouldShowAloAccess, setShouldShowAloAccess] = useState(true);
  // [REVISIT] Once headless -> create a menu in Shopify Admin and query it server-side
  const accountMenu = useMemo(
    () => [
      { key: prependLocaleToPath('/account'), text: 'Order History' },
      { key: prependLocaleToPath('/account?view=edit'), text: 'Account Information' },
      { key: prependLocaleToPath('/pages/access'), text: 'Alo Access' },
      { key: prependLocaleToPath('/account/addresses'), text: 'Shipping Information' },
      { key: prependLocaleToPath('/account?view=wishlist'), text: 'Wishlist' },
      { key: prependLocaleToPath('/pages/gift-card-balance'), text: 'Gift Card Balance' },
      { key: prependLocaleToPath('/account/logout'), text: 'Sign Out' },
    ],
    []
  );

  const processedAccountMenu = useMemo(
    () =>
      accountMenu.filter((item) => {
        if (!shouldShowAloAccess && item.key.includes('/pages/access')) return false;
        if (IS_NATIVE_APP_CLIENT && (item.key.includes('/account/logout') || item.key.includes('wishlist')))
          return false;
        return true;
      }),
    [shouldShowAloAccess, accountMenu]
  );

  useEffect(() => {
    const template = urlParams.get('view');
    const pathname = `${window.location.pathname}${template ? '?view=' + template : ''}`;
    const isLoyaltyEnabled = window.loyalty_settings?.isLoyaltyForDomesticEnabled;
    setShouldShowAloAccess(Boolean(isLoyaltyEnabled));

    const activeMenuItemIndex = processedAccountMenu.findIndex(
      (item) => pathname.toLowerCase() === item.key.toLowerCase()
    );
    setActiveKey(activeMenuItemIndex >= 0 ? activeMenuItemIndex : 0); // Default to order history
  }, [processedAccountMenu]);

  const shouldShowProProgramBlock = Boolean(customer?.tags?.map((tag) => tag.toLowerCase()).includes('pro-program'));

  if (activeKey === null || activeKey < 0) return null;

  return (
    <div className="headings">
      {shouldShowDropdownMenu ? (
        <>
          <div className="pb-xl mobile-account-menu">
            <p className="body">My Account:</p>
            <AloDropdown
              dropdownItems={processedAccountMenu}
              onChange={(item) => {
                window.location.href = item.key;
              }}
              placeholder=""
              wrapperClass="account-menu-dropdown"
              defaultValue={processedAccountMenu[activeKey]}
              popoverClass="account-menu-popover"
            />
          </div>
          {shouldShowProProgramBlock && <ProProgramBlock />}
        </>
      ) : (
        <>
          <h3 className="pb-4">{processedAccountMenu[activeKey]?.text}</h3>
          {shouldShowProProgramBlock && <ProProgramBlock />}
          <ul className="nav nav-sidebar">
            {processedAccountMenu.map((item, index) => (
              <li>
                <a href={item.key} className={`body uppercase ${activeKey === index ? 'active bold' : ''}`}>
                  {item.text}
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default AccountMenu;

const renderAccountMenu = () => {
  const customer = window.getCustomerInfo ? window.getCustomerInfo() : null;
  const mountNodes = document.querySelectorAll('[react-render-target="account-menu"]');
  if (mountNodes.length) mountNodes.forEach((node) => ReactDOM.render(<AccountMenu customer={customer} />, node));
  else console.warn('missing render target: account-menu');
};

export const InitAccountMenu = () => {
  renderAccountMenu();
};

const ProProgramBlock = () => {
  return (
    <p className="body pro-program-block">
      Pro Program Status:{' '}
      <span style={{ color: 'var(--success-medium)' }} className="body semibold">
        ACTIVE
      </span>
      <br />
      Use code <span className="body semibold">AYPROPROGRAM</span> to get your discount.
    </p>
  );
};
